import React, { useState } from "react";
import { Form, Button, Row, Col, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { useCreateStatsMutation } from "../../../redux/api/StatsApi/statsApi";
import { FaPlus, FaSave, FaArrowLeft, FaChartLine } from "react-icons/fa";
import "../Services/Services.css";
import LoaderSpinner from "../../../Components/LoaderSpinner";

const AddStats = () => {
  const navigate = useNavigate();
  const [createStats, { isLoading }] = useCreateStatsMutation();

  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    stat_one_title: "",
    stat_one_value: "",
    stat_two_title: "",
    stat_two_value: "",
    stat_three_title: "",
    stat_three_value: "",
    stat_four_title: "",
    stat_four_value: "",
    display_order: 0,
    status: true,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title?.trim()) newErrors.title = "Title is required";
    if (!formData.description?.trim())
      newErrors.description = "Description is required";

    // Check if at least some stats are provided
    if (!formData.stat_one_title?.trim())
      newErrors.stat_one_title = "Stat 1 title is required";
    if (formData.stat_one_value === "")
      newErrors.stat_one_value = "Stat 1 value is required";

    if (!formData.stat_two_title?.trim())
      newErrors.stat_two_title = "Stat 2 title is required";
    if (formData.stat_two_value === "")
      newErrors.stat_two_value = "Stat 2 value is required";

    if (!formData.stat_three_title?.trim())
      newErrors.stat_three_title = "Stat 3 title is required";
    if (formData.stat_three_value === "")
      newErrors.stat_three_value = "Stat 3 value is required";

    if (!formData.stat_four_title?.trim())
      newErrors.stat_four_title = "Stat 4 title is required";
    if (formData.stat_four_value === "")
      newErrors.stat_four_value = "Stat 4 value is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      await createStats(formData).unwrap();
      message.success("Stats created successfully!");
      setTimeout(() => navigate("/admin/stats"), 2000);
    } catch (error) {
      message.error(error.data?.error || "Failed to create stats");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="container py-4 fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaPlus className="text-primary me-2" />
            Add Stats
          </h2>
          <p className="text-muted mb-0">Define your strength indicators</p>
        </div>
        <Button
          variant="outline-secondary"
          className="d-flex align-items-center gap-2 shadow-sm rounded-pill px-4"
          onClick={() => navigate("/admin/stats")}
        >
          <FaArrowLeft /> Back to List
        </Button>
      </div>

      <Form onSubmit={handleSubmit}>
        <Card className="border-0  overflow-hidden mb-4">
          <Card.Body className="p-4 p-md-5">
            <h5 className="mb-4 text-secondary border-start border-4 border-primary ps-3">
              General Information
            </h5>
            <Row className="g-4">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Main Title <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    isInvalid={!!errors.title}
                    placeholder="e.g. Our Strengths"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.title}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Description <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    isInvalid={!!errors.description}
                    placeholder="Short overview of these statistics..."
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.description}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <h5 className="mt-5 mb-4 text-secondary border-start border-4 border-primary ps-3">
              Statistics Counters
            </h5>
            <Row className="g-4">
              <Col md={6}>
                <Card className="bg-light border-0">
                  <Card.Body>
                    <p className="fw-bold mb-3">
                      <FaChartLine className="me-2" /> Stat One
                    </p>
                    <Form.Group className="mb-3">
                      <Form.Label>Label</Form.Label>
                      <Form.Control
                        name="stat_one_title"
                        value={formData.stat_one_title}
                        onChange={handleChange}
                        isInvalid={!!errors.stat_one_title}
                        placeholder="e.g. Projects"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.stat_one_title}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Value</Form.Label>
                      <Form.Control
                        name="stat_one_value"
                        value={formData.stat_one_value}
                        onChange={handleChange}
                        isInvalid={!!errors.stat_one_value}
                        placeholder="e.g. 500"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.stat_one_value}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="bg-light border-0">
                  <Card.Body>
                    <p className="fw-bold mb-3">
                      <FaChartLine className="me-2" /> Stat Two
                    </p>
                    <Form.Group className="mb-3">
                      <Form.Label>Label</Form.Label>
                      <Form.Control
                        name="stat_two_title"
                        value={formData.stat_two_title}
                        onChange={handleChange}
                        isInvalid={!!errors.stat_two_title}
                        placeholder="e.g. Team size"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.stat_two_title}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Value</Form.Label>
                      <Form.Control
                        name="stat_two_value"
                        value={formData.stat_two_value}
                        onChange={handleChange}
                        isInvalid={!!errors.stat_two_value}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.stat_two_value}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="bg-light border-0">
                  <Card.Body>
                    <p className="fw-bold mb-3">
                      <FaChartLine className="me-2" /> Stat Three
                    </p>
                    <Form.Group className="mb-3">
                      <Form.Label>Label</Form.Label>
                      <Form.Control
                        name="stat_three_title"
                        value={formData.stat_three_title}
                        onChange={handleChange}
                        isInvalid={!!errors.stat_three_title}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.stat_three_title}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Value</Form.Label>
                      <Form.Control
                        name="stat_three_value"
                        value={formData.stat_three_value}
                        onChange={handleChange}
                        isInvalid={!!errors.stat_three_value}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.stat_three_value}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="bg-light border-0">
                  <Card.Body>
                    <p className="fw-bold mb-3">
                      <FaChartLine className="me-2" /> Stat Four
                    </p>
                    <Form.Group className="mb-3">
                      <Form.Label>Label</Form.Label>
                      <Form.Control
                        name="stat_four_title"
                        value={formData.stat_four_title}
                        onChange={handleChange}
                        isInvalid={!!errors.stat_four_title}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.stat_four_title}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Value</Form.Label>
                      <Form.Control
                        name="stat_four_value"
                        value={formData.stat_four_value}
                        onChange={handleChange}
                        isInvalid={!!errors.stat_four_value}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.stat_four_value}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <h5 className="mt-5 mb-4 text-secondary border-start border-4 border-primary ps-3">
              Settings
            </h5>
            <Row className="g-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    name="display_order"
                    value={formData.display_order}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="d-flex align-items-center pt-4">
                <Form.Check
                  type="switch"
                  id="stats-status"
                  label="Active / Visible"
                  name="status"
                  checked={formData.status}
                  onChange={handleChange}
                  className="fw-bold text-primary"
                />
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-3 mt-5">
              <Button
                variant="light"
                className="rounded-pill px-4"
                onClick={() => navigate("/admin/stats")}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                className="rounded-pill px-5 shadow-sm d-flex align-items-center gap-2"
              >
                <FaSave /> Save Stats
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Form>
    </div>
  );
};

export default AddStats;
